import { useEffect, useState } from 'react'
import { topicPhoto } from '@/lib/topicPhotos'
import { drawnScene } from '@/components/feed/FeedPhoto'
import type { PicStory } from '@/lib/picMatch'
import { useStoryPhoto } from '@/hooks/useStoryPhoto'

/**
 * A story's picture (Brian, pics.js and DEVELOPER-HANDOFF §4): the outlet's
 * photo with its credit; with none, the library photo matched to the story
 * (the same one its feed card shows; pass `story`); then the topic's stock photo; if that fails
 * too, a drawn scene. Never an empty box. Our pictures carry no label; only an
 * outlet photo is credited. The drawn scene is the feed's (FeedPhoto drawnScene).
 */
export function StoryPicture({ imageUrl, credit, topic, ratio = '16/10', radius = 16, className, showCredit = true, seedKey, story }: {
  imageUrl?: string | null
  /** The story, for matching a library photo when there is no outlet photo. Its id should be the headline id. */
  story?: PicStory | null
  credit?: string | null
  topic: string | null | undefined
  ratio?: string
  radius?: number
  className?: string
  showCredit?: boolean
  /** Shapes the drawn scene, so each story's is its own (pics.js). */
  seedKey?: string
}) {
  const lib = useStoryPhoto(imageUrl ? null : story)
  const sources = [imageUrl || lib, topicPhoto(topic)].filter(Boolean) as string[]
  const [i, setI] = useState(0)
  useEffect(() => setI(0), [imageUrl, lib, topic])
  const src = i < sources.length ? sources[i] : drawnScene(topic, seedKey ?? imageUrl ?? String(topic))
  const isOutlet = !!imageUrl && src === imageUrl

  return (
    <figure className={`spic${className ? ` ${className}` : ''}`} style={{ aspectRatio: ratio, borderRadius: radius }}>
      {src && <img src={src} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setI((n) => n + 1)} />}
      {src && isOutlet && showCredit && credit && <figcaption className="spic__credit">{credit}</figcaption>}
    </figure>
  )
}
